/**
 * CareScene ('Care') — the cozy follower-care room (the owner's "care for the follower like
 * Digimon World 1" wish, with every stress lever removed). A calm full-screen GB-styled room
 * showing the partner's PMD emotion PORTRAIT big + centered, its name + mood word, two soft
 * gauges (Happiness = the real friendship stat, Fullness), and four big warm buttons —
 * Feed · Praise · Play · Rest. Every tap is pure upside: the portrait swaps to a happy
 * emotion, a warm verb line pops, the gauges only ever ease UP, and a small heart flourish
 * plays. Nothing here can go red, run out, punish, or nag (the anti-DW1 contract).
 *
 * The renderer owns no rules: all care math lives in core/care.ts; GameSession.careAct raises
 * the REAL PokemonInstance.friendship + persists. This scene just presents + animates the
 * deltas it returns. Touch-first + full keyboard nav (arrows move the ▶ cursor, Enter acts,
 * Esc/Back returns) + phone-portrait reachable. Re-entry safe (Phaser reuses the instance).
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './ui/theme.js';
import { Panel } from './ui/Panel.js';
import { Button } from './ui/Button.js';
import { DialogBox } from './ui/DialogBox.js';
import { Toast } from './ui/Toast.js';
import { FocusRing } from './ui/FocusRing.js';
import { Layout, LAYOUT_CHANGE } from './layout/Layout.js';
import { moveGrid, type GridDir } from './ui/gridNav.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { getSession } from './session.js';
import { fitSprite } from './collection/parts.js';
import type { GameSession } from '@/session';
import type { SpriteResolver } from './sprites/index.js';
import { SpriteResolver as SpriteResolverImpl } from './sprites/index.js';
import { REGISTRY_SPRITES } from './PreloadScene.js';
import { careMood, FULLNESS_MAX, FULLNESS_FLOOR, FRIENDSHIP_MAX, type CareAction } from '@/core';
import type { Emotion, PokemonInstance } from '@/shared';

/** Launch payload: which mon to care for, and where to return. */
interface CareSceneData {
  readonly instanceId?: string;
  /** Scene key to return to on Back (defaults to Town). */
  readonly returnScene?: string;
}

/** The four warm actions, in display order, with their button labels. */
const ACTIONS: readonly { action: CareAction; label: string }[] = [
  { action: 'feed', label: 'Feed' },
  { action: 'praise', label: 'Praise' },
  { action: 'play', label: 'Play' },
  { action: 'rest', label: 'Rest' },
];

const PORTRAIT_SIZE = 132;

export class CareScene extends Phaser.Scene {
  private layout!: Layout;
  private reduced = false;
  private session!: GameSession;
  private resolver!: SpriteResolver;

  private instanceId = '';
  private returnScene = 'Town';
  private mon: PokemonInstance | null = null;
  private dexNo = 1;
  private displayName = 'your partner';

  private friendship = 0;
  private fullness = FULLNESS_FLOOR;
  private cosmeticId: string | null = null;
  private lastCaredEmotion: Emotion | null = null;

  private backdrop?: Phaser.GameObjects.Graphics;
  private card?: Panel;
  private portrait?: Phaser.GameObjects.Image;
  private portraitFrame?: Phaser.GameObjects.Graphics;
  private cosmetic?: Phaser.GameObjects.Text;
  private nameText?: Phaser.GameObjects.Text;
  private moodText?: Phaser.GameObjects.Text;
  private gauges?: Phaser.GameObjects.Graphics;
  private happyLabel?: Phaser.GameObjects.Text;
  private fullLabel?: Phaser.GameObjects.Text;
  private dialog?: DialogBox;
  private backBtn?: Button;
  private actionBtns: Button[] = [];
  private focusRing?: FocusRing;

  private focusIndex = 0; // 0..3 action buttons; 4 = Back
  private busy = false;
  private settleTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super('Care');
  }

  init(data: Partial<CareSceneData>): void {
    this.instanceId = data.instanceId ?? '';
    this.returnScene = data.returnScene ?? 'Town';
  }

  create(): void {
    this.resetState();
    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    this.reduced = isReducedMotion();
    this.session = getSession(this);
    this.resolver =
      (this.registry.get(REGISTRY_SPRITES) as SpriteResolver | undefined) ??
      new SpriteResolverImpl(this);

    // Resolve the target mon (explicit id, else the follower). Read its real friendship +
    // care fullness so the gauges start truthful.
    this.mon =
      (this.instanceId ? this.session.monById(this.instanceId) : null) ?? this.session.follower;
    if (this.mon) {
      this.instanceId = this.mon.id;
      this.friendship = this.mon.friendship;
      const species = this.session.bundle.index.speciesById(this.mon.speciesId);
      this.dexNo = species?.dexNo ?? 1;
      this.displayName = this.mon.nickname ?? species?.name ?? 'your partner';
      const rec = this.session.careRecordFor(this.mon.id);
      this.fullness = rec.fullness;
      this.cosmeticId = rec.cosmeticId;
    }

    this.buildUi();
    this.applyCosmetic();
    this.relayout();
    this.layout.on(LAYOUT_CHANGE, this.relayout, this);
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());

    // First portrait + flavor line at the resting mood.
    void this.showPortrait(this.mood());
    this.updateMoodText();
    this.dialog?.show([this.restingFlavor()]);

    fadeIn(this);
  }

  // ── build ────────────────────────────────────────────────────────────────────

  private buildUi(): void {
    this.backdrop = this.add.graphics();
    this.card = new Panel(this, 0, 0, { width: 100, height: 100, shadow: true });

    this.portraitFrame = this.add.graphics();
    this.portrait = this.add.image(0, 0, '__DEFAULT').setVisible(false);
    this.cosmetic = this.add
      .text(0, 0, '', { fontFamily: FONT.family, fontSize: `${FONT.size.lg}px` })
      .setOrigin(0.5)
      .setVisible(false);

    this.nameText = this.add
      .text(0, 0, this.displayName, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    this.moodText = this.add
      .text(0, 0, '', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0, 0.5);

    this.gauges = this.add.graphics();
    this.happyLabel = this.add
      .text(0, 0, '♥ Happiness', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0, 0.5);
    this.fullLabel = this.add
      .text(0, 0, '◍ Fullness', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0, 0.5);

    this.dialog = new DialogBox(this, 0, 0, {
      width: 340,
      height: 84,
      reducedMotion: this.reduced,
    });

    this.backBtn = new Button(this, 0, 0, {
      label: '‹ Back',
      variant: 'ghost',
      reducedMotion: this.reduced,
      onClick: () => this.goBack(),
    });

    this.actionBtns = ACTIONS.map(
      (a) =>
        new Button(this, 0, 0, {
          label: a.label,
          variant: a.action === 'rest' ? 'warm' : 'primary',
          reducedMotion: this.reduced,
          onClick: () => void this.doCare(a.action),
        }),
    );

    this.focusRing = new FocusRing(this, { depth: 60 });
  }

  // ── care action ────────────────────────────────────────────────────────────────

  private async doCare(action: CareAction): Promise<void> {
    if (this.busy || !this.mon) return;
    this.busy = true;
    const res = await this.session.careAct(this.instanceId, action);
    if (!this.scene.isActive()) return;
    if (!res) {
      this.busy = false;
      return;
    }
    this.friendship = res.newFriendship;
    this.fullness = res.newFullness;
    this.lastCaredEmotion = res.emotion;

    // Portrait swaps to the action's happy emotion, then settles back to the resting mood.
    void this.showPortrait(res.emotion);
    this.settleTimer?.remove(false);
    this.settleTimer = this.time.delayedCall(1500, () => {
      if (this.scene.isActive()) void this.showPortrait(this.mood());
    });

    this.updateGauges();
    this.updateMoodText();
    this.dialog?.show([`${this.displayName} ${res.verb}!`]);
    this.heartFlourish();

    new Toast(this, this.layout.centerX, this.layout.safe.y + SPACING.xl, {
      text: `${this.displayName} ${res.verb} 🍃`,
      reducedMotion: this.reduced,
      accent: COLORS.warm,
      width: Math.min(360, this.layout.safe.width - SPACING.lg * 2),
      duration: 1800,
    });

    this.busy = false;
  }

  /** Show a worn follower cosmetic (purely decorative) as a small glyph over the portrait. */
  private applyCosmetic(): void {
    if (!this.cosmetic) return;
    const glyph =
      this.cosmeticId === 'show-ribbon'
        ? '🎀'
        : this.cosmeticId === 'ember-sparkle'
          ? '✨'
          : this.cosmeticId === 'shrine-lantern'
            ? '🏮'
            : '';
    this.cosmetic.setText(glyph).setVisible(glyph !== '');
  }

  private mood(): Emotion {
    return careMood(this.friendship, this.fullness);
  }

  private restingFlavor(): string {
    if (this.friendship >= 220) return `${this.displayName} beams up at you — utterly devoted.`;
    if (this.friendship >= 140) return `${this.displayName} is happy at your side.`;
    if (this.fullness <= FULLNESS_FLOOR + 5)
      return `${this.displayName} looks content — a touch peckish, but cozy.`;
    return `${this.displayName} is happy to see you.`;
  }

  private updateMoodText(): void {
    const word =
      this.friendship >= 220
        ? 'devoted'
        : this.friendship >= 140
          ? 'happy'
          : this.fullness <= FULLNESS_FLOOR + 5
            ? 'content'
            : 'cheerful';
    this.moodText?.setText(`${this.moodEmojiFor(this.mood())} feeling ${word}`);
  }

  private moodEmojiFor(emotion: Emotion): string {
    switch (emotion) {
      case 'joyous':
        return '✨';
      case 'sigh':
        return '☁️';
      default:
        return '☺';
    }
  }

  // ── portrait ─────────────────────────────────────────────────────────────────

  private async showPortrait(emotion: Emotion): Promise<void> {
    const resolved = await this.resolver.portrait(this.dexNo, emotion).catch(() => null);
    if (!this.scene.isActive() || !this.portrait) return;
    if (resolved) {
      this.portrait.setTexture(resolved.key);
      fitSprite(this.portrait, PORTRAIT_SIZE);
      this.portrait.setVisible(true);
      this.positionPortrait();
    }
  }

  // ── flourish (reduced-motion aware) ──────────────────────────────────────────────

  private heartFlourish(): void {
    if (!this.portrait) return;
    const cx = this.portrait.x;
    const cy = this.portrait.y - PORTRAIT_SIZE / 2;
    if (this.reduced) return; // instant bars already updated; skip the animation
    for (let i = 0; i < 3; i++) {
      const heart = this.add
        .text(cx + (i - 1) * 14, cy, '♥', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.warm,
        })
        .setOrigin(0.5)
        .setDepth(80);
      this.tweens.add({
        targets: heart,
        y: cy - 44 - i * 6,
        alpha: { from: 1, to: 0 },
        duration: 900 + i * 120,
        ease: 'Sine.easeOut',
        onComplete: () => heart.destroy(),
      });
    }
    // A gentle happy bob of the portrait itself.
    this.tweens.add({
      targets: this.portrait,
      y: this.portrait.y - 8,
      duration: 160,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });
  }

  // ── keyboard ─────────────────────────────────────────────────────────────────

  private setupKeyboard(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    kb.on('keydown-LEFT', () => this.moveFocus('left'));
    kb.on('keydown-RIGHT', () => this.moveFocus('right'));
    kb.on('keydown-UP', () => this.moveFocus('up'));
    kb.on('keydown-DOWN', () => this.moveFocus('down'));
    kb.on('keydown-ENTER', () => this.activateFocus());
    kb.on('keydown-SPACE', () => this.activateFocus());
    kb.on('keydown-ESC', () => this.goBack());
  }

  /** Focusable order: the four actions (a 2-col grid), then Back as the last item. */
  private moveFocus(dir: GridDir): void {
    const count = this.actionBtns.length + 1; // +1 for Back
    const cols = 2;
    this.focusIndex = moveGrid(this.focusIndex, dir, count, cols);
    this.drawFocus();
  }

  private activateFocus(): void {
    if (this.focusIndex < this.actionBtns.length) {
      void this.doCare(ACTIONS[this.focusIndex]!.action);
    } else {
      this.goBack();
    }
  }

  private drawFocus(): void {
    const ring = this.focusRing;
    if (!ring) return;
    const target =
      this.focusIndex < this.actionBtns.length ? this.actionBtns[this.focusIndex] : this.backBtn;
    if (!target) {
      ring.hide();
      return;
    }
    ring.show(target.x, target.y, target.buttonWidth, target.buttonHeight, RADIUS.md);
    ring.bringToTop();
  }

  // ── navigation ─────────────────────────────────────────────────────────────────

  private goBack(): void {
    if (this.busy) return;
    this.busy = true;
    // Returning to Town, hand back the last care emotion so the trailing follower reacts.
    const data =
      this.returnScene === 'Town' && this.lastCaredEmotion
        ? { followerCared: this.lastCaredEmotion }
        : this.returnScene === 'TeamBoxes'
          ? { tab: 'team' }
          : undefined;
    goTo(this, this.returnScene, data ? { data } : {});
  }

  // ── layout ─────────────────────────────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const portrait = this.layout.isPortrait || safe.width < 560;

    this.drawBackdrop();

    // Heading (Back top-left, title centered top).
    this.backBtn?.setPosition(
      safe.x + SPACING.md + (this.backBtn.buttonWidth ?? 0) / 2,
      safe.y + SPACING.lg,
    );

    // The care card fills most of the safe area, leaving room for the button row below.
    const cardW = Math.min(safe.width - SPACING.md * 2, 460);
    const buttonRowH = portrait ? TOUCH_TARGET * 2 + SPACING.md : TOUCH_TARGET + SPACING.md;
    const cardH = Math.min(
      safe.height - SPACING.xxl - buttonRowH - SPACING.lg,
      portrait ? 340 : 260,
    );
    const cardY = safe.y + SPACING.xxl + cardH / 2;
    this.card?.setPosition(cx, cardY);
    this.card?.resize(cardW, cardH);

    // Portrait sits centered-left inside the card; name/mood/bars to its right.
    const cardLeft = cx - cardW / 2;
    const px = cardLeft + SPACING.lg + PORTRAIT_SIZE / 2;
    const py = cardY - (portrait ? cardH / 4 : 0);
    this.portraitTarget = { x: px, y: py };
    this.positionPortrait();

    const infoX = px + PORTRAIT_SIZE / 2 + SPACING.lg;
    this.nameText?.setPosition(infoX, cardY - cardH / 2 + SPACING.xl);
    this.moodText?.setPosition(infoX, cardY - cardH / 2 + SPACING.xl + FONT.size.lg);

    // Gauges under the name (or under the portrait on a narrow phone card).
    const gaugeX = portrait ? cardLeft + SPACING.lg : infoX;
    const gaugeTop = portrait ? py + PORTRAIT_SIZE / 2 + SPACING.md : cardY - SPACING.xs;
    this.gaugeLeft = gaugeX;
    this.gaugeTop = gaugeTop;
    this.gaugeWidth = portrait ? cardW - SPACING.lg * 2 : cardW - (infoX - cardLeft) - SPACING.lg;
    this.happyLabel?.setPosition(gaugeX, gaugeTop);
    this.fullLabel?.setPosition(gaugeX, gaugeTop + 34);
    this.updateGauges();

    // Buttons: a row of four (desktop) or a 2×2 grid (phone portrait), below the card.
    const buttonsTop = cardY + cardH / 2 + SPACING.lg;
    this.layoutButtons(cx, buttonsTop, cardW, portrait);

    // Flavor band (classic GB message box) just below the button row — clear of the card +
    // gauges, and reachably close to the actions rather than stranded at the screen bottom.
    const buttonBlockH = (portrait ? TOUCH_TARGET * 2 + SPACING.sm : TOUCH_TARGET) + SPACING.sm;
    const dialogY = Math.min(
      buttonsTop + buttonBlockH + SPACING.lg + 42,
      safe.y + safe.height - 42 - SPACING.sm,
    );
    this.dialog?.setPosition(cx, dialogY);
    this.drawFocus();
  }

  private portraitTarget = { x: 0, y: 0 };
  private gaugeLeft = 0;
  private gaugeTop = 0;
  private gaugeWidth = 200;

  private positionPortrait(): void {
    this.portrait?.setPosition(this.portraitTarget.x, this.portraitTarget.y);
    this.cosmetic?.setPosition(
      this.portraitTarget.x + PORTRAIT_SIZE / 2 - 8,
      this.portraitTarget.y - PORTRAIT_SIZE / 2 + 8,
    );
    this.drawPortraitFrame();
  }

  private layoutButtons(cx: number, top: number, cardW: number, portrait: boolean): void {
    const btns = this.actionBtns;
    if (btns.length === 0) return;
    const cols = portrait ? 2 : 4;
    const gap = SPACING.md;
    const totalW = Math.min(cardW, this.layout.safe.width - SPACING.md * 2);
    const btnW = Math.max(TOUCH_TARGET * 1.4, (totalW - gap * (cols - 1)) / cols);
    const rowH = TOUCH_TARGET + SPACING.sm;
    const startX = cx - totalW / 2 + btnW / 2;
    btns.forEach((b, i) => {
      const r = Math.floor(i / cols);
      const c = i % cols;
      b.setWidth(btnW);
      b.setPosition(startX + c * (btnW + gap), top + r * rowH);
    });
  }

  private updateGauges(): void {
    const g = this.gauges;
    if (!g) return;
    g.clear();
    const w = Math.max(80, this.gaugeWidth);
    const barH = 12;
    const x = this.gaugeLeft;
    // Happiness = friendship / 255 — a warm green fill, NEVER a red zone (spec).
    this.drawBar(
      g,
      x,
      this.gaugeTop + 14,
      w,
      barH,
      this.friendship / FRIENDSHIP_MAX,
      COLORS.hpGreen,
    );
    // Fullness — a soft blue fill resting at the floor (never alarmed).
    this.drawBar(g, x, this.gaugeTop + 48, w, barH, this.fullness / FULLNESS_MAX, COLORS.accent);
    // Contextual fullness word.
    this.fullLabel?.setText(
      this.fullness >= FULLNESS_MAX - 5
        ? '◍ Fullness — full'
        : this.fullness <= FULLNESS_FLOOR + 5
          ? '◍ Fullness — content'
          : '◍ Fullness',
    );
  }

  private drawBar(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    w: number,
    h: number,
    ratio: number,
    fill: number,
  ): void {
    const r = Math.max(0, Math.min(1, ratio));
    g.fillStyle(COLORS.hpTrack, 1);
    g.fillRoundedRect(x, y, w, h, h / 2);
    if (r > 0) {
      g.fillStyle(fill, 1);
      g.fillRoundedRect(x, y, Math.max(h, w * r), h, h / 2);
    }
    g.lineStyle(1, COLORS.hpFrame, 0.6);
    g.strokeRoundedRect(x, y, w, h, h / 2);
  }

  private drawBackdrop(): void {
    const g = this.backdrop;
    if (!g) return;
    const safe = this.layout.safe;
    g.clear();
    g.fillStyle(COLORS.surface, 1);
    g.fillRect(safe.x, safe.y, safe.width, safe.height);
    // A soft warm band — the cozy "care room" rug.
    g.fillStyle(COLORS.surfaceAlt, 0.5);
    g.fillRoundedRect(
      safe.x + SPACING.md,
      safe.y + safe.height * 0.28,
      safe.width - SPACING.md * 2,
      safe.height * 0.46,
      RADIUS.lg,
    );
    // Heading text drawn as a graphic-free label via nameText position; add a title.
    if (!this.titleText) {
      this.titleText = this.add
        .text(this.layout.centerX, safe.y + SPACING.lg, 'Partner Care', {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.textMuted,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    } else {
      this.titleText.setPosition(this.layout.centerX, safe.y + SPACING.lg);
    }
  }

  private titleText?: Phaser.GameObjects.Text;

  private drawPortraitFrame(): void {
    const g = this.portraitFrame;
    if (!g) return;
    const { x, y } = this.portraitTarget;
    const half = PORTRAIT_SIZE / 2 + SPACING.sm;
    g.clear();
    g.fillStyle(COLORS.boxFillInner, 1);
    g.fillRoundedRect(x - half, y - half, half * 2, half * 2, RADIUS.md);
    g.lineStyle(3, COLORS.boxBorder, 1);
    g.strokeRoundedRect(x - half, y - half, half * 2, half * 2, RADIUS.md);
  }

  // ── re-entry safety ──────────────────────────────────────────────────────────────

  private resetState(): void {
    this.backdrop = undefined;
    this.card = undefined;
    this.portrait = undefined;
    this.portraitFrame = undefined;
    this.cosmetic = undefined;
    this.nameText = undefined;
    this.moodText = undefined;
    this.gauges = undefined;
    this.happyLabel = undefined;
    this.fullLabel = undefined;
    this.dialog = undefined;
    this.backBtn = undefined;
    this.actionBtns = [];
    this.focusRing = undefined;
    this.titleText = undefined;
    this.focusIndex = 0;
    this.busy = false;
    this.mon = null;
    this.cosmeticId = null;
    this.lastCaredEmotion = null;
    this.settleTimer = undefined;
  }

  private teardown(): void {
    this.settleTimer?.remove(false);
    this.tweens.killAll();
    this.layout?.off(LAYOUT_CHANGE, this.relayout, this);
    this.input.keyboard?.removeAllListeners();
    this.focusRing?.destroy();
    this.dialog?.destroy();
    this.backBtn?.destroy();
    for (const b of this.actionBtns) b.destroy();
    this.card?.destroy();
    this.portrait?.destroy();
    this.portraitFrame?.destroy();
    this.cosmetic?.destroy();
    this.nameText?.destroy();
    this.moodText?.destroy();
    this.gauges?.destroy();
    this.happyLabel?.destroy();
    this.fullLabel?.destroy();
    this.backdrop?.destroy();
    this.titleText?.destroy();
  }
}
